<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header (matching create page) -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        {{ $route.name == "ProfilePage" ? "My Profile" : "Edit User" }}
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ $route.name == "ProfilePage" ? "Update your profile information" : "Modify user account details and permissions" }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button (hidden for sellers) -->
                    <v-btn
                      v-if="!isSeller"
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      v-if="$route.name != 'ProfilePage'"
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Users
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">User Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}
                </v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account</v-icon>
                    Basic Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.first_name"
                        label="First Name *"
                        placeholder="Enter first name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'First name is required']"
                        :error-messages="errors.first_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.last_name"
                        label="Last Name *"
                        placeholder="Enter last name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Last name is required']"
                        :error-messages="errors.last_name"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.middle_name"
                        label="Middle Name (Optional)"
                        placeholder="Enter middle name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.middle_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6" v-if="$route.name !== 'ProfilePage'">
                      <v-autocomplete
                        v-model="form.position_id"
                        :items="positionItems"
                        label="Position *"
                        placeholder="Select position"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Position is required']"
                        :error-messages="errors.position_id"
                        @change="UpdateUpline"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-briefcase</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Contact Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-card-account-mail</v-icon>
                    Contact Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.phone"
                        label="Phone Number *"
                        placeholder="Enter phone number"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Phone number is required']"
                        :error-messages="errors.phone"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-phone</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                    
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.email"
                        label="Email Address"
                        placeholder="Enter email address"
                        outlined
                        type="email"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[
                          v => !v || /.+@.+\..+/.test(v) || 'Email must be valid if provided',
                        ]"
                        :error-messages="errors.email"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-email</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Store Information Section (for Sellers) -->
                <div class="form-section mb-2" v-if="isSellerPosition()">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-store</v-icon>
                    Store Information
                  </h3>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.gst_no"
                        label="GST Number"
                        placeholder="Enter GST number"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.gst_no"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-file-document</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.store_name"
                        label="Store Name"
                        placeholder="Enter store name"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.store_name"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-store</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="form.drug_license_no"
                        label="Drug License Number"
                        placeholder="Enter drug license number"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.drug_license_no"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.store_type_id"
                        :items="storeTypeItems"
                        label="Store Type"
                        placeholder="Select store type"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :error-messages="errors.store_type_id"
                        class="professional-field"
                        hide-details="auto"
                        item-text="description"
                        item-value="id"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-tag</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                </div>

                <!-- Address Information Section (for Sellers) -->
                <div class="form-section mb-2" v-if="isSellerPosition()">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                    Address Information
                  </h3>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.address"
                        label="Full Address *"
                        placeholder="Enter complete address with landmark"
                        outlined
                        rows="3"
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Address is required']"
                        :error-messages="errors.address"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-home</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="4">
                      <v-autocomplete
                        v-model="form.state"
                        :items="stateItems"
                        label="State *"
                        placeholder="Select state"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'State is required']"
                        @change="onStateChange"
                        :loading="statesLoading"
                        :error-messages="errors.state"
                        class="professional-field"
                        hide-details="auto"
                        required
                        :menu-props="{ maxHeight: 420 }"
                        no-data-text="No states found"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-map</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.city"
                        label="City *"
                        placeholder="Enter city"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'City is required']"
                        :error-messages="errors.city"
                        class="professional-field"
                        hide-details="auto"
                        clearable
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-city</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <v-text-field
                        v-model="form.pincode"
                        label="Pincode *"
                        placeholder="Enter pincode"
                        type="number"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Pincode is required']"
                        :error-messages="errors.pincode"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-mailbox</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Account Settings Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-cog</v-icon>
                    Account Settings
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-account-check</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">Account Status</div>
                              <div class="text-caption grey--text">Set the status for this user account</div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              :disabled="!isEditMode"
                              class="ml-3"
                              :true-value="true"
                              :false-value="false"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip 
                              small 
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? 'Active' : 'Inactive' }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>


              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ hasValidationErrors() ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                      </v-icon>
                      {{ hasValidationErrors() ? 'Please fix validation errors' : 'Form validation passed' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>
                
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="save"
                      :loading="isLoading"
                      :disabled="!isEditMode || isLoading"
                      v-show="isEditMode"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Update {{ $route.name == "ProfilePage" ? "Profile" : "User" }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>
<script>
import axios from "@/axios";
export default {
  name: "UserEditPage",
  data() {
    return {
      form: {
        user_devices: [],
        user_locations: [],
      },
      valid: false,
      errors: {},
      isLoading: false,
      isEditMode: false, // Toggle between edit and read-only mode
      positionItems: [],
      storeTypeItems: [],
      statesLoading: false,
      citiesLoading: false,
      stateItems: [],
      cityItems: [],
      uplineUserItems: [],
      supervisorDownlined: [
        "Field Executive",
        "Merchandiser",
        "Promoter",
        "Van Sales",
      ],
      typeItems: [
        { id: "PENDING", text: "PENDING", value: 1 },
        { id: "APPROVED", text: "APPROVED", value: 0 },
      ],
      isDeleteDeviceDialogs: [],
      isDeleteLocationDialogs: [],
    };
  },
  async mounted() {
    await this.getMasters();
    await this.loadStates();
    await this.getData();
  },
  computed: {
    isSeller() {
      return this.$store.getters['auth/isSeller'];
    },
  },
  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },
    isSellerPosition() {
      // Check if current user's position is SELLER
      const currentPosition = this.positionItems.find(pos => pos.id === this.form.position_id);
      return currentPosition && currentPosition.text.toLowerCase().includes('seller');
    },
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
      }
    },

    async getMasters() {
      try {
        const response = await axios.get(`masters?context=user`);
        const masters = response.data;
        
        //Position
        masters.positions.forEach((position) => {
          this.positionItems.push({
            id: position.id,
            text: position.name,
            value: position.id,
          });
        });
        
        // Store types
        if (masters.store_types) {
          this.storeTypeItems = masters.store_types.map(storeType => ({
            id: storeType.id,
            description: storeType.description,
            value: storeType.id
          }));
        }
      } catch (error) {
        console.log(error);
        window.location.href = "/auth/login";
      }
    },

    async loadStates() {
      this.statesLoading = true;
      const fallbackStates = [
        'Andaman and Nicobar Islands','Andhra Pradesh','Arunachal Pradesh','Assam','Bihar',
        'Chandigarh','Chhattisgarh','Dadra and Nagar Haveli and Daman and Diu','Delhi','Goa',
        'Gujarat','Haryana','Himachal Pradesh','Jammu and Kashmir','Jharkhand','Karnataka',
        'Kerala','Ladakh','Lakshadweep','Madhya Pradesh','Maharashtra','Manipur','Meghalaya',
        'Mizoram','Nagaland','Odisha','Puducherry','Punjab','Rajasthan','Sikkim','Tamil Nadu',
        'Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'
      ];
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: 'India' })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        const apiStates = Array.isArray(json?.data?.states)
          ? json.data.states.map(s => (s?.name || '').trim()).filter(Boolean)
          : [];

        // Merge API + fallback to guarantee full coverage, then dedupe and sort
        const merged = Array.from(new Set([...apiStates, ...fallbackStates]))
          .sort((a, b) => a.localeCompare(b));

        this.stateItems = merged.map(name => ({ text: name, value: name }));
      } catch (error) {
        console.warn('State API failed, using local list:', error);
        this.stateItems = fallbackStates
          .slice()
          .sort((a, b) => a.localeCompare(b))
          .map(name => ({ text: name, value: name }));
      } finally {
        this.statesLoading = false;
      }
    },

    async onStateChange() {
      this.form.city = "";
      this.cityItems = [];
      if (this.form.state) {
        await this.loadCitiesForState(this.form.state);
      }
    },

    async loadCitiesForExistingState(stateName) {
      if (stateName) {
        await this.loadCitiesForState(stateName, true);
      }
    },

    async loadCitiesForState(stateName, keepExisting = false) {
      this.citiesLoading = true;
      try {
        const response = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ country: 'India', state: stateName })
        });
        const json = await response.json();
        this.cityItems = (json.data || []).map(city => ({ text: city, value: city }));
        if (keepExisting && this.form.city && !this.cityItems.find(c => c.value === this.form.city)) {
          this.cityItems.unshift({ text: this.form.city, value: this.form.city });
        }
      } catch (error) {
        this.cityItems = [];
        if (keepExisting && this.form.city) {
          this.cityItems = [{ text: this.form.city, value: this.form.city }];
        }
      } finally {
        this.citiesLoading = false;
      }
    },
    async getData() {
      try {
        this.isLoading = true;
        let encryptedId = this.$route.params.userId;
        const response = await axios.get(`/users/${encryptedId}`);
        
        this.form = response.data.data;
        // Normalize is_active to a real boolean — API may return 1/0
        this.form.is_active = !!this.form.is_active;
        console.log('User data loaded:', this.form);
        this.old_json = response.data.data;
        this.getUplineUsers();
        
        // Load cities for the existing state without clearing the city
        if (this.form.state) {
          await this.loadCitiesForExistingState(this.form.state);
        }
        
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
        window.location.href = "/auth/login";
        console.error("Error fetching data:", error);
      }
    },
    async save() {
      try {
        this.isLoading = true;

        // Use regular JSON for updates
        await axios.patch(`/users/${this.$route.params.userId}`, this.form);

        await this.saveUserTimestamp(this.old_json, this.form);
        this.isLoading = false;

        if (this.$route.name == "ProfilePage") {
          // Stay on the page: reload the saved data, refresh the logged-in user
          // (header / sidebar name), then confirm with a toast.
          await this.getData();
          await this.$store.dispatch("auth/attempt", localStorage.getItem("token"));
          this.isEditMode = false;
          this.$toast.success("Profile updated successfully");
        } else {
          // Same behaviour for editing any user: stay on the page, reload, then toast
          await this.getData();
          this.isEditMode = false;
          this.$toast.success("User updated successfully");
        }
      } catch (error) {
        this.isLoading = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error updating user:", error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        }
      }
    },
    Add_Empty_Device() {
      let user_device = {
        imei_no: "",
      };
      this.form.user_devices.push(user_device);
    },
    deleteDevice(user_device) {
      this.form.user_devices.splice(
        this.form.user_devices.indexOf(user_device),
        1
      );
      this.isDeleteDeviceDialogs = [];
    },
    Add_Empty_Location() {
      let user_location = {
        imei_no: "",
      };
      this.form.user_locations.push(user_location);
    },
    deleteLocation(user_location) {
      this.form.user_locations.splice(
        this.form.user_locations.indexOf(user_location),
        1
      );
      this.isDeleteLocationDialogs = [];
    },


    getStatusColor(status) {
      switch (Number(status)) {
        case 0:
          return "orange"; // Pending
        case 1:
          return "green"; // Approved
        case 2:
          return "red"; // Rejected
        default:
          return "grey";
      }
    },

    getStatusText(status) {
      switch (Number(status)) {
        case 0:
          return "Pending";
        case 1:
          return "Approved";
        case 2:
          return "Rejected";
        default:
          return "Unknown";
      }
    },

    UpdateUpline() {
      console.log("Position:" + this.form.position_id);

      let current = this.positionItems.find(
        (position) => position.id == this.form.position_id
      );
      if (
        this.supervisorDownlined.find((position) => position == current.text)
      ) {
        let upline = this.positionItems.find(
          (position) => position.text == "Supervisor"
        );
        if (upline) {
          this.form.upline_position_id = upline ? upline.id : null;
          this.getUplineUsers();
        }
      } else {
        let upline =
          this.positionItems[this.positionItems.indexOf(current) - 1];
        if (upline) {
          this.form.upline_position_id = upline ? upline.id : null;
          this.getUplineUsers();
        }
      }
    },
    async getUplineUsers() {
      this.uplineUserItems = [];
      let query = "position_id=" + this.form.upline_position_id;
      await axios
        .get(`users?${query}`)
        .then((users) => {
          //User
          users.data.data.forEach((user) => {
            this.uplineUserItems.push({
              id: user.id,
              text: user.user_name,
              value: user.id,
            });
          });
        })
        .catch(function (error) {
          console.log(error);
          // redirect to Home Page
          window.location.href = "/auth/login";
        });
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Documents Container */
.documents-container {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #fafafa;
}

.document-item .v-card {
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.document-item .v-card:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .form-title {
    font-size: 0.9rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Read-Only Mode Styling */
.professional-field >>> .v-input--is-disabled {
  opacity: 0.7;
}

.professional-field >>> .v-input--is-disabled .v-input__slot {
  background-color: #f5f5f5 !important;
  border-color: #e0e0e0 !important;
}

.professional-field >>> .v-input--is-disabled .v-label {
  color: #757575 !important;
}

.professional-field >>> .v-input--is-disabled input {
  color: #616161 !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
